import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api, downloadBlob } from '../../api/client'
import type { PatchEvidence, PatchEvidenceRow } from '../../api/types'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable, type Column } from '../DataTable'
import { chipClass, KevChip, SeverityChip } from './labels'
import { tipoEccezioneLabel } from './Exceptions'

function statoLabel(r: PatchEvidenceRow): string {
  switch (r.status) {
    case 'open': return r.exception ? t('aperta, con eccezione') : t('aperta')
    case 'fixed': return r.installation ? t('corretta da CENSO') : t('corretta')
    case 'superseded': return t('superata')
    default: return t('macchina eliminata')
  }
}

/**
 * The audit evidence (patch_evidence_service): for a CVE, a patch or a machine,
 * every detection with when it was found, who decided, when it was installed
 * and verified, the exception and the ticket — exported for the auditor.
 */
export function Evidence() {
  const [cve, setCve] = useState('')
  const [dati, setDati] = useState<PatchEvidence | null>(null)
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const filtro = () => (/^CVE-/i.test(cve.trim()) ? { cve: cve.trim() } : { patch_key: cve.trim() })
  const cerca = async () => {
    setLavoro(true)
    setErrore(null)
    try {
      setDati(await api.patchEvidence(filtro()))
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  const scarica = async (fmt: 'xlsx' | 'pdf') => {
    try {
      const blob = await downloadBlob(api.patchEvidenceExportPath(filtro(), fmt))
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `censo-patch-evidenza.${fmt}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setErrore(e)
    }
  }
  const colonne: Column<PatchEvidenceRow>[] = [
    { key: 'machine', header: t('Macchina'), value: (r) => r.machine, render: (r) => <Link to={`/cis/${r.ci_id}`}>{r.machine || r.ci_id.slice(0, 8)}</Link> },
    { key: 'patch', header: t('Patch'), value: (r) => r.patch, render: (r) => <><span className="mono">{r.patch}</span> <SeverityChip severity={r.severity} />{r.exploited && <> <KevChip /></>}</> },
    { key: 'found', header: t('Rilevata il'), type: 'date', value: (r) => r.first_detected_at, render: (r) => formatDateTime(r.first_detected_at) },
    { key: 'status', header: t('Stato'), value: (r) => statoLabel(r), filter: 'select',
      render: (r) => <><span className={chipClass(r.status === 'fixed' ? 'ok' : r.status === 'open' ? 'warn' : 'muted')}>{statoLabel(r)}</span>{r.fixed_at && <div className="text-muted patch-sub">{formatDateTime(r.fixed_at)}</div>}</> },
    { key: 'sla', header: t('Entro lo SLA'), value: (r) => (r.within_sla ? t('sì') : t('no')), filter: 'select',
      render: (r) => <span className={chipClass(r.within_sla ? 'ok' : 'warn')}>{t('{n} di {s} giorni', { n: r.days_open, s: r.sla_days })}</span> },
    { key: 'install', header: t('Installazione'), value: (r) => r.installation?.approved_by ?? '',
      render: (r) => (r.installation ? (
        <span>{t('approvata da {chi}', { chi: r.installation.approved_by ?? '—' })} · {formatDateTime(r.installation.applied_at)}
          {r.installation.verification && <div className="text-muted patch-sub">{t('verifica: {v}', { v: r.installation.verification })}</div>}</span>
      ) : <span className="muted">—</span>) },
    { key: 'exception', header: t('Eccezione'), value: (r) => r.exception?.kind ?? '',
      render: (r) => (r.exception ? <span title={r.exception.reason}>{tipoEccezioneLabel(r.exception.kind)} · {r.exception.decided_by ?? ''}</span> : '') },
    { key: 'ticket', header: t('Ticket'), value: (r) => r.tickets.map((x) => x.number).join(' '),
      render: (r) => r.tickets.map((x) => <a key={x.number} href={x.url} target="_blank" rel="noreferrer" style={{ marginRight: 6 }}>{x.number}</a>) },
  ]
  return (
    <Card title={t('Evidenza per l’audit')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Per una CVE (CVE-2026-1234) o una patch (kb:KB5031234, pkg:openssl): ogni macchina che ne è stata colpita, quando è stata trovata, chi ha chiesto e approvato l’installazione, quando è stata installata e verificata, l’eccezione e il ticket se c’erano, e la cronologia. È la prova da dare a un revisore (NIS2, DORA), in Excel o PDF.')}
      </p>
      <form className="patch-toolbar" onSubmit={(e) => { e.preventDefault(); void cerca() }}>
        <input type="text" value={cve} onChange={(e) => setCve(e.target.value)} placeholder={'CVE-2026-1234 · kb:KB5031234 · pkg:openssl'} aria-label={t('CVE o patch')} style={{ minWidth: 320 }} />
        <button type="submit" disabled={lavoro || !cve.trim()}>{lavoro ? t('Cerco…') : t('Cerca')}</button>
        {dati && dati.rows.length > 0 && <>
          <button type="button" className="secondary" onClick={() => void scarica('xlsx')}>{t('Excel')}</button>
          <button type="button" className="secondary" onClick={() => void scarica('pdf')}>PDF</button>
        </>}
      </form>
      <ErrorBanner error={errore} />
      {lavoro && <Spinner />}
      {dati && (dati.rows.length === 0
        ? <p className="muted">{t('Nessuna macchina risulta aver avuto questa vulnerabilità o questa patch mancante.')}</p>
        : <DataTable rows={dati.rows} columns={colonne} rowKey={(r) => r.detection_id} conteggio={{ nome: t('rilevamenti') }} />)}
    </Card>
  )
}
